<script setup lang="ts">
import { EditTripStepType } from '@apps/main/new-desktop/modules/editTripModal';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppBasePlaceholder, AppSvgIcon } from '@libs/ui';

import { useTripOverviewModalsStore } from '../../stores/tripOverviewModalsStore';

const { t } = i18n.global;
const tripOverviewModalsStore = useTripOverviewModalsStore();
const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();

interface AddDestinationPlaceholderProps {
  origin: 'topTest' | 'bottom';
}
const props = defineProps<AddDestinationPlaceholderProps>();

function openDestinationModal(): void {
  statsigEventsStore.logTripOverviewAddDestinationClicked({
    origin: props.origin,
  });
  tripOverviewModalsStore.openEditTripModal({
    stepType: EditTripStepType.AddDestination,
    shouldAddDestinationOnTop: props.origin === 'topTest',
  });
}
</script>

<template>
  <AppBasePlaceholder
    class="add-destination-placeholder"
    :class="{ mobile: browserStore.isSmallerThan900px }"
    :title="t('overview_placeholder.add_destination.title')"
    :description="t('overview_placeholder.add_destination.description')"
    :is-small-device="browserStore.isSmallerThan900px"
    :icon-properties="{
      name: 'locationMarkOutlined',
      color: 'color-brand-violet',
    }"
    @click="openDestinationModal"
  >
    <template #actions>
      <div class="plus-in-circle">
        <AppSvgIcon
          class="plus-icon"
          name="plus"
          size="24"
          @click="openDestinationModal"
        />
      </div>
    </template>
  </AppBasePlaceholder>
</template>

<style scoped lang="scss">
.add-destination-placeholder {
  border: 1px dashed var(--color-brand-pop);
  background-color: var(--color-information-light);
  @include on-hover {
    cursor: pointer;
    background-color: var(--color-light-hover);
    .plus-in-circle {
      background-color: var(--color-brand-violet);
      .plus-icon {
        color: var(--color-white);
      }
    }
  }
  .plus-in-circle {
    --size: 44px;
    transition: background-color var(--transition-short);
    width: var(--size);
    height: var(--size);
    border-radius: 100%;
    background-color: var(--color-white);
    border: 1px solid var(--color-neutral-30);
    display: flex;
    .plus-icon {
      transition: background-color var(--transition-short);
      margin: auto;
    }
  }

  @include for-less-or-equal-than-1200-px {
    margin: 0 var(--spacing-24);
  }

  @include for-less-or-equal-than-600-px {
    margin: 0 var(--spacing-12);
  }

  &.mobile {
    :deep(.left-content) {
      align-self: center;
      display: flex;
    }
    :deep(.main-content) {
      flex-direction: row;
    }
  }
}
</style>
